import apiClient from './client';
import { ApiResponse } from '@/types';

/**
 * Overview report data
 */
export interface OverviewReport {
  period: {
    days: number;
    since: string;
    until: string;
  };
  users: {
    total: number;
    active: number;
    byRole: Record<string, number>;
  };
  repositories: {
    total: number;
    active: number;
    withWebhooks: number;
  };
  commits: {
    total: number;
    additions: number;
    deletions: number;
    uniqueAuthors: number;
  };
  reviews: {
    total: number;
    completed: number;
    averageScore: number;
    totalIssues: number;
  };
  standups: {
    total: number;
    submitted: number;
    missed: number;
    excused: number;
    averageClarity: number;
    averageProgress: number;
    submissionRate: number;
  };
  alerts: {
    total: number;
    bySeverity: Record<string, number>;
    byType: Record<string, number>;
    unacknowledged: number;
    unresolved: number;
  };
  aiCosts: {
    totalCost: number;
    requestCount: number;
    totalTokens: number;
  };
}

/**
 * Developer report data
 */
export interface DeveloperReport {
  user: {
    id: string;
    username: string;
    displayName: string;
    email: string;
    role: string;
  };
  period: {
    days: number;
    since: string;
    until: string;
  };
  commits: {
    total: number;
    additions: number;
    deletions: number;
    filesChanged: number;
    byDay: Array<{
      date: string;
      count: number;
      additions: number;
      deletions: number;
    }>;
    recent: Array<{
      id: string;
      shortSha: string;
      title: string;
      committedAt: string;
      repoId?: { name: string };
    }>;
  };
  standups: {
    total: number;
    submitted: number;
    missed: number;
    excused: number;
    averageClarity: number;
    averageProgress: number;
    submissionRate: number;
  };
  reviews: {
    total: number;
    averageScore: number;
    totalIssues: number;
  };
}

/**
 * Team report data
 */
export interface TeamReport {
  period: {
    days: number;
    since: string;
    until: string;
  };
  developers: Array<{
    user: {
      id: string;
      username: string;
      displayName: string;
      role: string;
    };
    commits: {
      count: number;
      additions: number;
      deletions: number;
    };
    standups: {
      submitted: number;
      missed: number;
      rate: number;
    };
    reviews: {
      count: number;
      averageScore: number;
    };
  }>;
  summary: {
    totalDevelopers: number;
    totalCommits: number;
    averageCommitsPerDev: number;
    averageStandupRate: number;
    averageReviewScore: number;
  };
}

/**
 * Get overview report
 */
export async function getOverviewReport(days = 30): Promise<OverviewReport> {
  const response = await apiClient.get<ApiResponse<OverviewReport>>('/reports/overview', {
    params: { days },
  });
  return response.data.data as OverviewReport;
}

/**
 * Get developer report
 */
export async function getDeveloperReport(userId: string, days = 30): Promise<DeveloperReport> {
  const response = await apiClient.get<ApiResponse<DeveloperReport>>(`/reports/developer/${userId}`, {
    params: { days },
  });
  return response.data.data as DeveloperReport;
}

/**
 * Get team report
 */
export async function getTeamReport(days = 30): Promise<TeamReport> {
  const response = await apiClient.get<ApiResponse<TeamReport>>('/reports/team', {
    params: { days },
  });
  return response.data.data as TeamReport;
}

/**
 * Generated report type
 */
export type ReportType = 'daily' | 'weekly' | 'monthly';

/**
 * Generated report data
 */
export interface GeneratedReport {
  _id: string;
  type: ReportType;
  title: string;
  status: 'generating' | 'completed' | 'failed' | 'sent';
  periodStart: string;
  periodEnd: string;
  projectId?: {
    _id: string;
    name: string;
    code: string;
  };
  scope: 'all' | 'project';
  summary: {
    highlights: string[];
    concerns: string[];
    recommendations: string[];
  };
  metrics: {
    totalCommits: number;
    totalLinesChanged: number;
    activeUsers: number;
    avgCodeQuality: number;
    standupCompletionRate: number;
    alertsGenerated: number;
    alertsResolved: number;
    aiCostUsd: number;
  };
  topPerformers: Array<{
    userId: { _id: string; username: string; displayName: string };
    score: number;
    highlights: string[];
  }>;
  concerns: Array<{
    userId: { _id: string; username: string; displayName: string };
    issues: string[];
    severity: 'low' | 'medium' | 'high';
  }>;
  delivery: Array<{
    method: 'email' | 'rocketchat';
    recipients: string[];
    sentAt?: string;
    status: 'pending' | 'sent' | 'failed';
    error?: string;
  }>;
  generatedAt?: string;
  generationDurationMs?: number;
  createdAt: string;
}

/**
 * Get generated reports
 */
export async function getGeneratedReports(params?: {
  type?: ReportType;
  status?: string;
  startDate?: string;
  endDate?: string;
  projectId?: string;
  limit?: number;
  offset?: number;
}): Promise<{ reports: GeneratedReport[]; total: number }> {
  const response = await apiClient.get<
    ApiResponse<{ reports: GeneratedReport[]; pagination: { total: number } }>
  >('/reports/generated', { params });
  return {
    reports: response.data.data?.reports || [],
    total: response.data.data?.pagination?.total || 0,
  };
}

/**
 * Get generated report by ID
 */
export async function getGeneratedReport(reportId: string): Promise<GeneratedReport> {
  const response = await apiClient.get<ApiResponse<GeneratedReport>>(`/reports/generated/${reportId}`);
  return response.data.data as GeneratedReport;
}

/**
 * Get generated report HTML content
 */
export async function getGeneratedReportHtml(reportId: string): Promise<string> {
  const response = await apiClient.get<ApiResponse<{ htmlContent: string }>>(
    `/reports/generated/${reportId}/html`
  );
  return response.data.data?.htmlContent || '';
}

/**
 * Generate daily report
 */
export async function generateDailyReport(projectId?: string): Promise<{
  success: boolean;
  reportId?: string;
  error?: string;
  duration: number;
}> {
  const response = await apiClient.post<
    ApiResponse<{ success: boolean; reportId?: string; error?: string; duration: number }>
  >('/reports/generate/daily', { projectId });
  return response.data.data as {
    success: boolean;
    reportId?: string;
    error?: string;
    duration: number;
  };
}

/**
 * Generate weekly report
 */
export async function generateWeeklyReport(projectId?: string): Promise<{
  success: boolean;
  reportId?: string;
  error?: string;
  duration: number;
}> {
  const response = await apiClient.post<
    ApiResponse<{ success: boolean; reportId?: string; error?: string; duration: number }>
  >('/reports/generate/weekly', { projectId });
  return response.data.data as {
    success: boolean;
    reportId?: string;
    error?: string;
    duration: number;
  };
}

/**
 * Send report email
 */
export async function sendReportEmail(reportId: string): Promise<{ success: boolean; message: string }> {
  const response = await apiClient.post<ApiResponse<{ success: boolean; message: string }>>(
    `/reports/generated/${reportId}/send`
  );
  return response.data.data as { success: boolean; message: string };
}
